// Pure helpers + shared constants for the VisitReport wizard. Extracted from
// VisitReportWizard.tsx (size budget). No JSX, no engine state — just the
// step-vocabulary constants, predicates, and the derive functions the
// orchestrator + sub-components consume. The wizard's behaviour is unchanged.

import { useMemo } from "react";
import type { MobilityStatus } from "@hcm/core";
import { type StepDescriptor } from "./WizardStepIndicator.js";
import type { CrossSectionError } from "./CrossSectionErrorList.js";
import type { FormState } from "../lib/visit-report-types.js";
import type { SectionName } from "../api/visit-report-api.js";
import {
  type WizardStep, type SectionStep,
} from "../lib/visit-report-conditional-rules.js";
import { useAutoSave } from "../hooks/use-auto-save.js";
import { useSectionValidation, type UseSectionValidationResult } from "../hooks/use-section-validation.js";
import { validateSection } from "../lib/visit-report-section-validators.js";
import { getFieldSection } from "../lib/visit-report-error-mapping.js";

export const STEP_LABELS: Record<WizardStep, string> = {
  "visit-type": "Visit type",
  vitals:       "Vitals",
  mobility:     "Mobility",
  clinical:     "Clinical",
  wellbeing:    "Wellbeing",
  medication:   "Medication",
  notes:        "Notes",
  review:       "Review",
};

export const FALLBACK_FIRST_STEP: WizardStep = "vitals";
export const VISIT_TYPE_STEP_ID: WizardStep = "visit-type";
export const REVIEW_STEP_ID: WizardStep = "review";

export function toDescriptors(steps: readonly WizardStep[]): ReadonlyArray<StepDescriptor & { id: WizardStep }> {
  return steps.map((id) => ({ id, label: STEP_LABELS[id] }));
}

export function isSectionStep(step: WizardStep): step is SectionStep {
  return step !== VISIT_TYPE_STEP_ID && step !== REVIEW_STEP_ID;
}

/** Bus passed down to section components for blur + debounced-numeric save dispatch. */
export interface SectionAutoSaveBus {
  onFieldBlur: () => void;
  onNumericChange: () => void;
}

export type ServerErrorsBySection = ReadonlyMap<SectionStep, ReadonlyMap<string, readonly string[]>>;

export interface ChromeSaveState {
  lastSavedAt: Date | null;
  hasFailedSections: boolean;
  isSaving: boolean;
}

export function deriveChromeSaveState(
  status: ReturnType<typeof useAutoSave>["status"],
  currentStep: WizardStep,
  isSubmitting: boolean,
): ChromeSaveState {
  const isSection = isSectionStep(currentStep);
  return {
    lastSavedAt: isSection ? status.lastSavedAt[currentStep as SectionStep] ?? null : null,
    hasFailedSections: status.failedSections.size > 0,
    isSaving: (isSection && status.pendingSections.has(currentStep as SectionStep)) || isSubmitting,
  };
}

export interface NavState {
  descriptors: ReadonlyArray<StepDescriptor & { id: WizardStep }>;
  currentIdx: number;
  isFirstStep: boolean;
  isReviewStep: boolean;
  isVisitTypeStep: boolean;
  submitDisabledReason: string | null;
  showMobilityOverrideToggle: boolean;
}

interface DeriveNavInput {
  steps: readonly WizardStep[];
  currentStep: WizardStep;
  completedSteps: ReadonlySet<string>;
  baselineMobility: MobilityStatus | null | undefined;
  /** Q9 S6 / D9 9b — incomplete-but-visited sections drive submit-disable. */
  incompleteSteps: ReadonlySet<string>;
}

function buildSubmitDisabledReason(
  isReviewStep: boolean, steps: readonly WizardStep[],
  completedSteps: ReadonlySet<string>, incompleteSteps: ReadonlySet<string>,
): string | null {
  if (!isReviewStep) return null;
  const requiredSectionCount = steps.filter(isSectionStep).length;
  if (completedSteps.size < requiredSectionCount) {
    return "Some sections are still incomplete — visit each step before submitting.";
  }
  if (incompleteSteps.size > 0) {
    const labels = [...incompleteSteps].map((s) => STEP_LABELS[s as WizardStep] ?? s).join(", ");
    return `Fix incomplete fields in: ${labels}`;
  }
  return null;
}

export function deriveNavState(input: DeriveNavInput): NavState {
  const { steps, currentStep, completedSteps, baselineMobility, incompleteSteps } = input;
  const descriptors = toDescriptors(steps);
  const currentIdx = steps.indexOf(currentStep);
  const isReviewStep = currentStep === REVIEW_STEP_ID;
  return {
    descriptors,
    currentIdx,
    isFirstStep: currentIdx <= 0,
    isReviewStep,
    isVisitTypeStep: currentStep === VISIT_TYPE_STEP_ID,
    submitDisabledReason: buildSubmitDisabledReason(isReviewStep, steps, completedSteps, incompleteSteps),
    showMobilityOverrideToggle: baselineMobility === "independent",
  };
}

// Q9 S6 / D9 9b — visited sections that haven't passed the client gate
// AND don't have lingering server errors are "complete"; everything else
// stays in incompleteSteps so the indicator + submit gate can react.
function computeIncompleteSteps(
  steps: readonly WizardStep[], form: FormState,
  serverFieldErrors: ServerErrorsBySection,
): ReadonlySet<string> {
  const set = new Set<string>();
  for (const step of steps) {
    if (!isSectionStep(step)) continue;
    const sec = step as SectionStep;
    const sv = validateSection(sec, form);
    const serverErrs = serverFieldErrors.get(sec);
    if (!sv.isComplete || (serverErrs?.size ?? 0) > 0) set.add(step);
  }
  return set;
}

// Q9 S6 — convert the per-section server-error map into the flat list
// the CrossSectionErrorList consumes at the review step.
function buildCrossSectionErrors(
  serverFieldErrors: ServerErrorsBySection,
): readonly CrossSectionError[] {
  const out: CrossSectionError[] = [];
  for (const [, fieldMap] of serverFieldErrors) {
    for (const [field, msgs] of fieldMap) {
      const section = getFieldSection(field);
      if (!section) continue;
      for (const msg of msgs) {
        out.push({ message: msg, involvedFields: [{ section, fieldName: field }] });
      }
    }
  }
  return out;
}

// Q9 S7 — only forward pendingFocus when its section matches the active
// step; otherwise a cross-section error click could stale-focus the wrong
// section's container after step transitions.
export function pendingFocusForStep(
  pendingFocus: { section: SectionStep; fieldName: string } | null,
  currentStep: WizardStep,
): string | null {
  if (!pendingFocus || pendingFocus.section !== currentStep) return null;
  return pendingFocus.fieldName;
}

export function buildSectionBus(
  currentStep: WizardStep,
  autoSave: ReturnType<typeof useAutoSave>,
  currentSectionPayload: () => Record<string, unknown>,
): SectionAutoSaveBus {
  // Numeric onChange fires SYNCHRONOUSLY inside the input's onChange — at
  // that point React hasn't committed the setState yet, so reading
  // formRef.current would capture the value-before-this-keystroke (the
  // typed-too-fast-and-the-last-char-was-lost class of bug). Defer with
  // setTimeout(0) so the read happens AFTER the commit + formRef-sync
  // useEffect. Blur fires in its own event tick (focus change), so the
  // commit has already landed by then; blur reads inline.
  return {
    onFieldBlur: () => {
      if (!isSectionStep(currentStep)) return;
      void autoSave.dispatchFieldBlur(currentStep as SectionStep, currentSectionPayload());
    },
    onNumericChange: () => {
      if (!isSectionStep(currentStep)) return;
      setTimeout(() => {
        autoSave.dispatchDebouncedNumeric(currentStep as SectionStep, currentSectionPayload());
      }, 0);
    },
  };
}

// Q9 S6 — derive validation views (current section + cross-section + indicator).
export interface ValidationViews {
  currentValidation: UseSectionValidationResult;
  incompleteSteps: ReadonlySet<string>;
  crossSectionErrors: readonly CrossSectionError[];
}

export function useValidationViews(
  steps: readonly WizardStep[], currentStep: WizardStep, form: FormState,
  serverFieldErrors: ServerErrorsBySection,
): ValidationViews {
  const currentSectionStep = isSectionStep(currentStep) ? currentStep as SectionStep : "vitals";
  const currentServerErrs = serverFieldErrors.get(currentSectionStep) ?? new Map();
  const currentValidation = useSectionValidation(currentSectionStep, form, currentServerErrs);
  const incompleteSteps = useMemo(
    () => computeIncompleteSteps(steps, form, serverFieldErrors),
    [steps, form, serverFieldErrors],
  );
  const crossSectionErrors = useMemo(
    () => buildCrossSectionErrors(serverFieldErrors),
    [serverFieldErrors],
  );
  return { currentValidation, incompleteSteps, crossSectionErrors };
}

// SectionName re-exported for the engine's extractSectionPayload typing convenience.
export type { SectionName };
